import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MessageService } from 'primeng/api';
import { finalize, forkJoin, Observable, of } from 'rxjs';
import { ControlType, SearchControl } from 'bpt-ui-library/bpt-search';
import { ActionItemSearch } from '../models/action-item-search';
import { UsersService } from '../../shared/services/users.service';
import { ActionItemSearchResult } from '../models/action-item-search-result';
import { ActionItemRegisterConstants } from '../action-item-register-constants';
import { ActionItemRegisterService } from '../services/action-item-register.service';
import { ActionItemLookUp } from '../../change-control/change-control-details/models/action-item-lookup';
import { ActionItemPanelListComponent } from '../action-item-panel-list/action-item-panel-list.component';



@Component({
  selector: 'app-action-item-search',
  templateUrl: './action-item-search.component.html',
  styleUrls: ['./action-item-search.component.scss']
})
export class ActionItemSearchComponent implements OnInit {
  @Input() enableCheckBoxes: boolean = false;
  @Output() emitSelectedData = new EventEmitter();
  actionItemSearchResult: ActionItemSearchResult[] = [];
  isSearch = false;
  placeHolder: string = ActionItemRegisterConstants.ActionItemSearch_Place_Holder_Text;
  isScopeMasterDataLoaded = false;
  isUsersMasterDataLoaded = false;
  filterData: any[] = [];
  actionItemMasterData: any = {};
  scopeList: any = [];
  isMasterDataLoaded = false;
  userList: any = [];
  actionItemSearchData: ActionItemSearch = new ActionItemSearch;
  searchControl: SearchControl[] = [];
  displayDataCollected: any;
  lookupData!: ActionItemLookUp;
  isLookupDataLoaded: boolean = false;
  errorText: string = '';

  eQMSScopeLookupData: any[] = [];
  spinnerOn: boolean = false;

  @ViewChild(ActionItemPanelListComponent)
  private actionItemPanelList!: ActionItemPanelListComponent;


  constructor(private messageService: MessageService, private actionItemRegisterService: ActionItemRegisterService, 
    private usersService: UsersService) { }

  ngOnInit(): void {
    this.initialize();
  }

  initialize() {
    forkJoin({
      lookUpData: this.actionItemRegisterService.getActionItemLookUpDetails(),
      scopeList: this.actionItemRegisterService.getScopesForActionItem(),
      usersData: this.actionItemRegisterService.getusers()
    }).subscribe({
      next: ({ lookUpData, scopeList, usersData }) => {
        // Master Lookup data for Change Control
        this.lookupData = lookUpData;
        this.loadActionItemSearchFilterOptions();
        this.populateEQMSScopeList(scopeList); // eQMS Scope data
        this.populateUsersList(usersData);
      },
      error: (error) => {
        console.error('Error while fetching data:', error);
      },
    });
    // To get Due date from BPT Search
    this.searchControl.push(
      {
        controlType: ControlType.Datepicker,
        label: 'Due Date',
        fieldName: 'dueDate',
        enableRange: true,
        value: [],
        order: 3,
      });
  }

  private populateEQMSScopeList(locationsData: any): void {
    for (let dataItem of locationsData) {
      this.scopeList.push({
        value: dataItem.eQMSScopeId,
        name: dataItem.eQMSScopeName,
      });
    }

    this.searchControl.push({
      controlType: ControlType.List,
      label: 'eQMS Scope(s)',
      fieldName: 'eQMSScope',
      multiSelect: true,
      options: this.scopeList,
      value: undefined,
      labelField: 'name',
      valueField: 'value',
      showFilter: true,
      order: 1,
    });
    this.isScopeMasterDataLoaded = true;
  }


  loadActionItemSearchFilterOptions() {
    this.searchControl.push(
      {
        controlType: ControlType.List,
        label: 'Status',
        fieldName: 'status',
        options: this.lookupData.status,
        multiSelect: true,
        showFilter: true,
        value: [1],
        labelField: 'name',
        valueField: 'value',
        order: 2
      },
      {
        controlType: ControlType.List,
        label: 'Type',
        fieldName: 'type',
        options: this.lookupData.type,
        multiSelect: true,
        value: undefined,
        labelField: 'name',
        valueField: 'value',
        showFilter: true,
        order: 6,
      });
    this.isMasterDataLoaded = true;
  }

  getAdvancedSearchResults = (filters: Array<any>, searchText: string): Observable<number> => {
    this.resetActionItemSearchData();
    this.filterData = filters;
    if (searchText !== null && searchText != undefined) this.actionItemSearchData.searchText = searchText;
    filters.forEach(filterControl => {
      switch (filterControl.columnName) {
        case "eQMSScope":
          this.actionItemSearchData.eQMSScopes = filterControl.values
          break;
        case "status":
          if (filterControl.values != undefined)
            this.actionItemSearchData.status = filterControl.values.map(String)
          break;
        case "dueDate":
          this.actionItemSearchData.dueDate = filterControl.values
          break;
        case "assignedUser":
          this.actionItemSearchData.assignedUser = filterControl.values
          break;
        case "verifier":
          this.actionItemSearchData.verifier = filterControl.values
          break;
        case "type":
          if (filterControl.values != undefined)
            this.actionItemSearchData.type = filterControl.values.map(String)
          break;
      }
    });

    const isAllValuesNullOrUndefined = this.areAllValuesNullOrUndefined(this.actionItemSearchData);
    if (isAllValuesNullOrUndefined) {
      this.messageService.add({
        severity: ActionItemRegisterConstants.Severity_Info,
        summary: `${ActionItemRegisterConstants.ActionItemSearchEmpty_Filters}`,
      });
      return of(0);
    }

    let totalRecordCount = 0;
    this.actionItemRegisterService.getActionItemSearch(this.actionItemSearchData).
     pipe(finalize(() => {
      this.isSearch = true;
    }))
      .subscribe((result: { records: any[], totalRecordCount: number }): void => {
        this.actionItemSearchResult = result.records;
        totalRecordCount = result.totalRecordCount;
      });
    return of(totalRecordCount);
  };

  private areAllValuesNullOrUndefined(data: any): boolean {
    return Object.entries(data).every(([key, value]) => {
      if (key === ActionItemRegisterConstants.ActionItem_Search_Text) {
        return typeof value === 'string' && value.trim() === '';
      }
      if (key === ActionItemRegisterConstants.ActionItem_Search_Status_Text) {
        return Array.isArray(value) && value.length === 0;
      }
      if (key === ActionItemRegisterConstants.ActionItem_Search_Type_Text) {
        return Array.isArray(value) && value.length === 0 || value == undefined;
      }
      if (key === ActionItemRegisterConstants.ActionItem_Search_DueDate_Text) {
        return Array.isArray(value) && value.length === 0 || value == undefined;
      }
      return value === undefined;
    });
  }

  getSearchResults = (searchText: string): Observable<number> => {
    this.resetActionItemSearchData();
    if (searchText !== null && searchText != undefined) this.actionItemSearchData.searchText = searchText;
    this.filterData = [];
    let totalRecordCount = 0;

    this.actionItemRegisterService.getActionItemSearch(this.actionItemSearchData).pipe(finalize(() => {
      this.isSearch = true;
    })).subscribe((result: { records: any[], totalRecordCount: number }): void => {
        this.actionItemSearchResult = result.records;
        totalRecordCount = result.totalRecordCount;
      });

    
    return of(totalRecordCount);
  };


  getChildeQMSScopes(nodes: any) {
    nodes.forEach((childNode: any) => {
      this.scopeList.push({
        value: childNode.name,
        name: childNode.name
      })
      if (childNode.children) this.getChildeQMSScopes(childNode.children);
    })
  }

  linkedItemsSelectedData(event: any) {
    this.displayDataCollected = event;
    this.emitSelectedData.emit(this.displayDataCollected);
  }

  resetActionItemSearchData() {
    this.actionItemSearchData.eQMSScopes = undefined;
    this.actionItemSearchData.status = undefined;
    this.actionItemSearchData.assignedUser = undefined;
    this.actionItemSearchData.verifier = undefined;
    this.actionItemSearchData.dueDate = undefined;
    this.actionItemSearchData.type = undefined;
    this.actionItemSearchData.searchText = undefined;
  }

  getActionItemSelection() {
    this.actionItemPanelList.clearSelection();
  }

  private populateUsersList = (usersData: any): void => {
    this.userList = usersData.map((dataItem: any) => ({
      value: `${dataItem.name}`,
      name: `${dataItem.name}`,
    }));
    this.searchControl.push({
      controlType: ControlType.List,
      label: 'Assigned User',
      fieldName: 'assignedUser',
      multiSelect: true,
      options: this.userList,
      value: undefined,
      labelField: 'name',
      valueField: 'value',
      showFilter: true,
      order: 4,
    });

    this.searchControl.push({
      controlType: ControlType.List,
      label: 'Verifier',
      fieldName: 'verifier',
      multiSelect: true,
      options: this.userList,
      value: undefined,
      labelField: 'name',
      valueField: 'value',
      showFilter: true,
      order: 5,
    });
    this.isUsersMasterDataLoaded = true;
  }

  convertDateFormat(values: any){
    if(values!==null && values!== undefined && values.length==2)
      return values;
    else if(values!==null && values!== undefined && typeof(values) === "string"){
      let effectiveDateSelected: Date = new Date(values);
      let effectiveDate:Date[] = [];
      effectiveDate.push(effectiveDateSelected);
      return effectiveDate;
    }
  }

}
